/**
 * MIDI Event Merger - handles merging live MIDI with file playback
 */

import { MidiAudioHandler } from './midiAudioHandler';

export class MidiEventMerger {
  private liveActiveNotes = new Set<number>();
  private fileActiveNotes = new Set<number>();
  private liveSustainState: boolean | null = null;
  private fileSustainState: boolean = false; // Add file sustain tracking
  private pausedFileCallback?: (data: number[]) => void;

  constructor(private originalGetMIDIMessage: (data: number[], restoreOnly?: boolean) => void,
    private audioHandler?: MidiAudioHandler
  ) {}

  setPausedFileCallback(callback: (data: number[]) => void) {
    this.pausedFileCallback = callback;
  }
  
  // Add methods for sustain state management
  updateFileSustainState(state: boolean) {
    this.fileSustainState = state;
  }
  
  getFinalSustainState(): boolean {
    // Live sustain overrides file sustain off
    return this.liveSustainState === true || this.fileSustainState;
  }
  
  getFileSustainState(): boolean {
    return this.fileSustainState;
  }
  
  getLiveSustainState(): boolean | null {
    return this.liveSustainState;
  }

  // Pitches whose key is physically down on the MIDI keyboard right now.
  // File-playback state changes must never move these out of activeNotes -
  // whether a key is held is a fact about the player, not about the file.
  getLiveActiveNotes(): number[] {
    return Array.from(this.liveActiveNotes);
  }
  
  handleLiveMidi(data: number[]) {
    // Track BEFORE deciding whether to emit. A note-off that gets suppressed
    // below still means the player's key is up; skipping the bookkeeping would
    // leave the note owned by a source that has let go of it, forever. See the
    // ownership note on shouldEmitLiveEvent.
    this.trackLiveEvent(data);

    if (!this.shouldEmitLiveEvent(data)) return;

    if (this.audioHandler) {
      this.audioHandler.handleMidiForAudio(data, false, false); // Live MIDI is not precomputed
    }
    this.originalGetMIDIMessage(data);
  }
  
  // restoreOnly: the event describes a note that is ALREADY sounding and must
  // stay that way - it only needs to reach the right note list. There is no
  // onset here, so no sample is struck and no key is flashed; both would be
  // wrong for a note that never stopped.
  handleFileMidi(data: number[], isPrecomputed: boolean = false, isNoteOn: boolean = false, isPaused: boolean = false, restoreOnly: boolean = false) {
    // Track BEFORE deciding whether to emit, for the same reason as live MIDI:
    // a suppressed note-off still means the file has released that note.
    // Only while playing, though - paused file notes go to toggledNotes and are
    // not meant to own a pitch against the live keyboard.
    if (!isPaused) {
      this.trackFileEvent(data);
    }

    if (!this.shouldEmitFileEvent(data)) return;

    if (this.audioHandler && !restoreOnly) {
      this.audioHandler.handleMidiForAudio(data, isPrecomputed, isNoteOn);
    } else if (this.audioHandler) {
      // Not struck, but its key is down all the same
      this.audioHandler.trackKey(data);
    }

    // Route to different callback based on pause state
    // When paused, file MIDI should update toggledNotes instead of activeNotes
    if (isPaused === true && this.pausedFileCallback) {
      this.pausedFileCallback(data);
    } else {
      this.originalGetMIDIMessage(data, restoreOnly);
    }
  }
  
  // Live and file can hold the same pitch at once, and Tone.Sampler's
  // triggerRelease releases EVERY voice for a note - so one source letting go
  // would silence the other's note too. A note-off is therefore emitted only
  // when the other source is not also holding that pitch. This decides whether
  // the note is released, never whether it is tracked: the caller tracks first,
  // so the source that let go stops owning the pitch either way.
  private shouldEmitLiveEvent(data: number[]): boolean {
    const status = data[0] & 0xF0;

    if (status === 144 && data[2] > 0) {
      return true; // Live note on - always pass through
    } else if (status === 128 || (status === 144 && data[2] === 0)) {
      return !this.fileActiveNotes.has(data[1]); // File still holding it - let it ring
    } else if (data[0] === 176 && data[1] === 64) {
      return true; // Live sustain - always pass through
    }

    return true;
  }

  private shouldEmitFileEvent(data: number[]): boolean {
    const status = data[0] & 0xF0;

    if (status === 144 && data[2] > 0) {
      return true; // File note on - always pass through
    } else if (status === 128 || (status === 144 && data[2] === 0)) {
      return !this.liveActiveNotes.has(data[1]); // Live still holding it - let it ring
    } else if (data[0] === 176 && data[1] === 64) {
      // Only pass through file sustain if live sustain isn't overriding
      return this.liveSustainState !== true;
    }

    return true;
  }
  
  private trackLiveEvent(data: number[]) {
    const status = data[0] & 0xF0;
    
    if (status === 144 && data[2] > 0) {
      this.liveActiveNotes.add(data[1]);
    } else if (status === 128 || (status === 144 && data[2] === 0)) {
      this.liveActiveNotes.delete(data[1]);
    } else if (data[0] === 176 && data[1] === 64) {
      this.liveSustainState = data[2] >= 64;
    }
  }
  
  private trackFileEvent(data: number[]) {
    const status = data[0] & 0xF0;
    
    if (status === 144 && data[2] > 0) {
      this.fileActiveNotes.add(data[1]);
    } else if (status === 128 || (status === 144 && data[2] === 0)) {
      this.fileActiveNotes.delete(data[1]);
    }
  }
  
  clearFileState() {
    this.fileActiveNotes.clear();
    this.fileSustainState = false; // Reset file sustain state
  }

  clearFileSustainOnly() {
    this.fileSustainState = false; // Reset only file sustain, keep fileActiveNotes for conflict detection
  }

  clearAllSustainStates() {
    this.fileSustainState = false;
    this.liveSustainState = null; // Reset to null (no live sustain override)
  }
}